/*
  Glimot shared helpers (small, loaded deferred on every page).
  - Glimot.announce(message): polite screen-reader announcement.
  - Glimot.productData(handle): live product details via the internal `glimot-product-data`
    section (Section Rendering API), cached in memory for the current page only.
  - Glimot.addToCart({ id, quantity, properties }): adds one line and updates the cart UI the same
    way Craft's product form does, for the side drawer, pop-up or cart page.
  Nothing here writes personalisation text to device storage.
*/
(() => {
  if (window.Glimot && window.Glimot.ready) return;
  const config = window.glimot || {};
  const routesCfg = config.routes || {};
  const productCache = new Map();

  function announce(message) {
    const region = document.getElementById('GlimotLiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  }

  function productUrl(handle) {
    const root = routesCfg.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}products/${encodeURIComponent(handle)}`;
  }

  function productData(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache.has(handle)) return productCache.get(handle);
    const promise = fetch(`${productUrl(handle)}?section_id=glimot-product-data`)
      .then((response) => {
        if (!response.ok) return null;
        return response.text();
      })
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('[data-g-product-data]');
        return script ? JSON.parse(script.textContent) : null;
      })
      .catch(() => null);
    productCache.set(handle, promise);
    return promise;
  }

  function cartUi() {
    if (document.getElementById('main-cart-items') && document.getElementById('main-cart-footer')) {
      return { type: 'page', component: document.querySelector('cart-items') };
    }
    const component = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (component) return { type: component.tagName === 'CART-DRAWER' ? 'drawer' : 'notification', component };
    return { type: 'none', component: null };
  }

  /**
   * Add one line to the cart without leaving the page.
   * @returns {Promise<{ok: boolean, message?: string, response?: object}>}
   */
  function addToCart({ id, quantity = 1, properties = null }, { openCart = true } = {}) {
    const ui = cartUi();
    const body = { id, quantity };
    if (properties && Object.keys(properties).length) body.properties = properties;

    let sections = [];
    if (ui.type === 'page' && ui.component?.getSectionsToRender) {
      sections = ui.component.getSectionsToRender();
      body.sections = sections.map((section) => section.section);
    } else if (ui.component?.getSectionsToRender) {
      body.sections = ui.component.getSectionsToRender().map((section) => section.id);
    } else {
      body.sections = ['cart-icon-bubble'];
    }
    body.sections_url = window.location.pathname;

    return fetch(`${routesCfg.cartAdd || window.routes?.cart_add_url || '/cart/add'}`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const message = response.description || response.message || config.strings?.error;
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, { source: 'glimot', productVariantId: id, errors: message, message });
          }
          return { ok: false, message };
        }

        if (ui.type === 'page') {
          sections.forEach((section) => {
            const host = document.getElementById(section.id);
            if (!host || !response.sections?.[section.section]) return;
            const target = host.querySelector(section.selector) || host;
            const doc = new DOMParser().parseFromString(response.sections[section.section], 'text/html');
            const source = doc.querySelector(section.selector);
            if (source) target.innerHTML = source.innerHTML;
          });
          document.getElementById('main-cart-footer')?.classList.remove('is-empty');
          document.querySelector('cart-items')?.classList.remove('is-empty');
          // Keep the (closed) side drawer in step too, if the store uses it.
          document.querySelector('cart-drawer-items')?.onCartUpdate?.();
        } else if (ui.component && openCart) {
          if (ui.type === 'drawer') ui.component.classList.remove('is-empty');
          ui.component.renderContents(response);
        } else if (response.sections?.['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          const doc = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
          const source = doc.querySelector('.shopify-section');
          if (bubble && source) bubble.innerHTML = source.innerHTML;
        }

        if (typeof publish === 'function') {
          // On the cart page the sections were already re-rendered from this response, so the
          // source tells Craft's cart-items not to fetch them again.
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: ui.type === 'page' ? 'cart-items' : 'glimot',
            productVariantId: id,
            cartData: response,
          });
        }
        document.dispatchEvent(new CustomEvent('glimot:cart-changed', { detail: { variantId: id } }));
        return { ok: true, response, cartType: ui.type };
      })
      .catch(() => ({ ok: false, message: config.strings?.error }));
  }

  /**
   * Add the contents of a real product form, exactly as Craft's product form does: multipart
   * FormData, no manual Content-Type (the browser sets the boundary). Every field is sent unchanged —
   * theme personalisation, vendor app properties[...] and attributes[...], hidden fields and files.
   * @param {FormData} formData
   * @returns {Promise<{ok: boolean, message?: string, response?: object}>}
   */
  function addFormData(formData) {
    const request = fetchConfig('javascript');
    request.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete request.headers['Content-Type'];
    request.body = formData;
    return fetch(`${routesCfg.cartAdd || window.routes?.cart_add_url || '/cart/add'}`, request)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const message = response.description || response.message || config.strings?.error;
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, { source: 'glimot', productVariantId: formData.get('id'), errors: message, message });
          }
          return { ok: false, message };
        }
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'glimot', productVariantId: formData.get('id'), cartData: response });
        }
        document.dispatchEvent(new CustomEvent('glimot:cart-changed', { detail: { variantId: formData.get('id') } }));
        return { ok: true, response };
      })
      .catch(() => ({ ok: false, message: config.strings?.error }));
  }

  /** Change a cart line by key (quantity 0 removes it). */
  function changeLine(key, quantity) {
    return fetch(`${routesCfg.cartChange || window.routes?.cart_change_url || '/cart/change'}`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({ id: key, quantity }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.errors || state.status) return { ok: false, message: state.errors || state.description };
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'glimot', cartData: state });
        }
        document.dispatchEvent(new CustomEvent('glimot:cart-changed'));
        return { ok: true, cart: state };
      })
      .catch(() => ({ ok: false, message: config.strings?.error }));
  }

  function formatTemplate(template, values) {
    return Object.keys(values).reduce((text, key) => text.split(`[${key}]`).join(values[key]), template || '');
  }

  window.Glimot = Object.assign(window.Glimot || {}, {
    ready: true,
    config,
    announce,
    productUrl,
    productData,
    addToCart,
    addFormData,
    changeLine,
    formatTemplate,
  });
})();
